import type { StoreOrder } from "@medusajs/types";
import {
  queryOptions,
  type UseQueryResult,
  useQuery,
} from "@tanstack/react-query";
import { medusa } from "#/lib/medusa";

const ORDER_FIELDS =
  "id,display_id,status,payment_status,fulfillment_status,total,subtotal,currency_code,created_at,customer_id,metadata,*items,*shipping_address,*billing_address,*shipping_methods,*fulfillments,fulfillments.labels.id,fulfillments.labels.tracking_number,fulfillments.labels.tracking_url,fulfillments.labels.label_url";

type OrderListResponse = { orders: StoreOrder[]; count: number };

function isOrderLookupResponse(
  value: unknown,
): value is { order: StoreOrder | null } {
  return typeof value === "object" && value !== null && "order" in value;
}

export const orderKeys = {
  all: ["storefront", "orders"] as const,
  myList: (limit: number, offset: number) =>
    ["storefront", "orders", "my-list", limit, offset] as const,
  detail: (orderId: string) =>
    ["storefront", "orders", "detail", orderId] as const,
  lookup: (displayId: string, email: string) =>
    ["storefront", "orders", "lookup", displayId, email] as const,
};

export const orderQueries = {
  myList: (limit: number, offset: number) =>
    queryOptions({
      queryKey: orderKeys.myList(limit, offset),
      queryFn: async (): Promise<OrderListResponse> => {
        const res = await medusa.store.order.list({
          limit,
          offset,
          fields: ORDER_FIELDS,
        });
        return { orders: res.orders, count: res.count };
      },
    }),
  detail: (orderId: string | undefined) =>
    queryOptions({
      queryKey: orderKeys.detail(orderId ?? ""),
      queryFn: async (): Promise<StoreOrder | null> => {
        if (!orderId) return null;
        const { order } = await medusa.store.order.retrieve(orderId, {
          fields: ORDER_FIELDS,
        });
        return order;
      },
      enabled: !!orderId,
      refetchInterval: (query) => {
        const data = query.state.data;
        if (!data) return false;
        const paymentInFlight =
          data.payment_status === "awaiting" ||
          data.payment_status === "requires_action";
        const fulfillmentInFlight =
          data.fulfillment_status !== "delivered" &&
          data.status !== "canceled" &&
          data.status !== "completed";
        return paymentInFlight || fulfillmentInFlight ? 3_000 : false;
      },
    }),
  /**
   * Guest order lookup. The core GET /store/orders list route is customer-auth
   * scoped, so the custom /store/order-lookup route resolves an order by
   * display_id + email (the email is bound server-side) and returns it or null.
   */
  lookup: (displayId: string | undefined, email: string | undefined) => {
    const trimmedId = displayId?.trim() ?? "";
    const trimmedEmail = email?.trim().toLowerCase() ?? "";
    return queryOptions({
      queryKey: orderKeys.lookup(trimmedId, trimmedEmail),
      queryFn: async (): Promise<StoreOrder | null> => {
        const numeric = Number.parseInt(trimmedId.replace(/^#/, ""), 10);
        if (!Number.isFinite(numeric) || !trimmedEmail) return null;
        const params = new URLSearchParams({
          display_id: String(numeric),
          email: trimmedEmail,
        });
        const res = await medusa.client.fetch<unknown>(
          `/store/order-lookup?${params.toString()}`,
          { method: "GET" },
        );
        return isOrderLookupResponse(res) ? res.order : null;
      },
      enabled: trimmedId.length > 0,
    });
  },
};

export function useMyOrders(opts?: {
  enabled?: boolean;
  limit?: number;
  offset?: number;
}): UseQueryResult<OrderListResponse> {
  const limit = opts?.limit ?? 20;
  const offset = opts?.offset ?? 0;
  const options = orderQueries.myList(limit, offset);
  return useQuery({ ...options, enabled: opts?.enabled ?? true });
}

export function useOrder(
  orderId: string | undefined,
): UseQueryResult<StoreOrder | null> {
  return useQuery(orderQueries.detail(orderId));
}

export function useOrderByDisplayId(
  displayId: string | undefined,
  email: string | undefined,
): UseQueryResult<StoreOrder | null> {
  return useQuery(orderQueries.lookup(displayId, email));
}
